/**
 * heybalu mobile navigation
 */
document.addEventListener('click', (event) => {
  /* Entries with children expand in place instead of sliding to a second panel. */
  const toggle = event.target.closest('.hb-nav [data-hb-toggle]');

  if (toggle) {
    const item = toggle.parentElement;
    const collapse = toggle.nextElementSibling;
    const open = item.classList.toggle('is-open');

    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (collapse) {
      collapse.toggleAttribute('inert', !open);
    }

    return;
  }

  /* The drawer contains triggers for other overlays. Without this, both drawers
     would stay open on top of each other. */
  const trigger = event.target.closest('[aria-controls]');

  if (!trigger) {
    return;
  }

  const drawer = trigger.closest('.hb-nav');

  if (!drawer || !drawer.hasAttribute('open') || trigger.getAttribute('aria-controls') === drawer.id) {
    return;
  }

  /* The search opens inside the menu instead (see below), so the menu stays open. */
  if (trigger.getAttribute('aria-controls') === 'search-drawer' && document.documentElement.classList.contains('hb-search-from-nav')) {
    return;
  }

  drawer.dispatchEvent(new CustomEvent('dialog:force-close', { bubbles: true, cancelable: true, composed: true }));
});

/* Start from a clean state the next time the menu opens. */
document.addEventListener('dialog:after-hide', (event) => {
  const drawer = event.target.closest?.('.hb-nav');

  if (!drawer) {
    return;
  }

  drawer.querySelectorAll('.is-open').forEach((item) => {
    item.classList.remove('is-open');
    item.querySelector('[data-hb-toggle]')?.setAttribute('aria-expanded', 'false');
    item.querySelector('.hb-nav__collapse')?.setAttribute('inert', '');
  });
});

/* --------------------------------------------------------------------------------------------
 * Search inside the menu.
 *
 * Tapping the search field in the menu used to close the menu and slide the search drawer in
 * from the side. Now the menu stays open with its top bar (close, logo, cart) in place, and the
 * search drawer unfolds right below that bar, over the menu's link list. Closing the search
 * (its own close button) goes back to the menu; the menu's close button closes both.
 *
 * The search drawer is the regular one from sections/search-drawer.liquid: this only places it.
 * The class on <html> switches the placement CSS in heybalu-mobile-nav.css and makes
 * assets/hb-search.js open the drawer from the top. It is set in the capture phase, because the
 * drawer reads its opening direction the moment the trigger's click reaches it.
 *
 * Typing right away: one tap on the menu's search field is enough. iOS Safari raises the keyboard
 * only for a focus() inside the tap itself, but the search field cannot take focus yet at that
 * moment: the drawer is still closed. So the tap focuses an invisible stand-in field in the menu,
 * which brings up the keyboard, and focus moves on to the real field as soon as the drawer can
 * take it. Anything typed in between is carried over. The stand-in sits inside the menu so the
 * menu's focus trap leaves it alone, and at 16px so iOS does not zoom in on it.
 *
 * Closing with the menu's close button: the search leaves to the left together with the menu
 * instead of folding up. CLOSE_LEFT_CLASS makes assets/hb-search.js report "left" as the drawer's
 * direction, which the theme's hide animation reads; both drawers then run the same animation.
 * ------------------------------------------------------------------------------------------ */
(() => {
  const MODE_CLASS = 'hb-search-from-nav';
  const CLOSE_LEFT_CLASS = 'hb-search-close-left';
  const root = document.documentElement;

  const getNav = () => document.querySelector('.hb-nav[open]');
  const getSearch = () => document.getElementById('search-drawer');

  /* Place the search exactly over the menu's content, below its top bar. */
  const measure = (nav) => {
    const content = nav.shadowRoot?.querySelector('[part="content"]');
    const bar = nav.querySelector('.hb-nav__bar');

    if (!content || !bar) {
      return false;
    }

    const box = content.getBoundingClientRect();
    const top = bar.getBoundingClientRect().bottom;

    root.style.setProperty('--hb-sfn-top', `${Math.round(top)}px`);
    root.style.setProperty('--hb-sfn-left', `${Math.round(box.left)}px`);
    root.style.setProperty('--hb-sfn-width', `${Math.round(box.width)}px`);
    root.style.setProperty('--hb-sfn-height', `${Math.round(box.bottom - top)}px`);

    return true;
  };

  const reset = () => {
    root.classList.remove(MODE_CLASS, CLOSE_LEFT_CLASS);
    ['--hb-sfn-top', '--hb-sfn-left', '--hb-sfn-width', '--hb-sfn-height'].forEach((name) => root.style.removeProperty(name));
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.hb-nav [aria-controls="search-drawer"]');
    const nav = trigger && getNav();
    const search = getSearch();

    if (!nav || !search || search.hasAttribute('open')) {
      return;
    }

    if (measure(nav)) {
      root.classList.add(MODE_CLASS);
    }

    handOverFocus(primeKeyboard(trigger));
  }, true);

  /* Focused within the tap, so the phone's keyboard opens (see above). */
  const primeKeyboard = (trigger) => {
    const proxy = document.createElement('input');

    proxy.type = 'search';
    proxy.tabIndex = -1;
    proxy.setAttribute('aria-hidden', 'true');
    proxy.setAttribute('autocomplete', 'off');
    proxy.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;'
      + 'opacity:0;font-size:16px;pointer-events:none;';
    trigger.after(proxy);
    proxy.focus({ preventScroll: true });

    return proxy;
  };

  /* Move focus to the real field once the opening drawer can take it, then drop the stand-in. */
  const handOverFocus = (proxy) => {
    const input = getSearch()?.querySelector('input[type="search"], input[name="q"]');
    let frames = 0;

    const step = () => {
      if (input && getSearch()?.hasAttribute('open')) {
        /* Focus we grant, not keyboard navigation: no focus ring (see assets/hb-search.js). */
        input.setAttribute('data-hb-quiet-focus', '');
        input.focus({ preventScroll: true });

        if (document.activeElement === input) {
          if (proxy.value) {
            input.value += proxy.value;
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }

          proxy.remove();
          return;
        }
      }

      /* Give up after about 1.5s; the drawer focuses the field itself after opening. */
      if (++frames < 90) {
        requestAnimationFrame(step);
      } else {
        proxy.remove();
      }
    };

    requestAnimationFrame(step);
  };

  /* The menu's close button closes the search too. Pointer events, because while the search is
     open its focus trap swallows the tap's click outside of it. */
  document.addEventListener('pointerup', (event) => {
    if (!root.classList.contains(MODE_CLASS) || !event.target.closest('.hb-nav .hb-nav__bar [is="close-button"]')) {
      return;
    }

    root.classList.add(CLOSE_LEFT_CLASS);
    getSearch()?.hide();
    getNav()?.hide();
  }, true);

  document.addEventListener('toggle', (event) => {
    if (!root.classList.contains(MODE_CLASS)) {
      return;
    }

    /* Menu closed some other way: take the search with it. */
    if (event.target.matches?.('.hb-nav') && !event.target.hasAttribute('open')) {
      root.classList.add(CLOSE_LEFT_CLASS);
      getSearch()?.hide();
    }
  });

  document.addEventListener('dialog:after-hide', (event) => {
    if (event.target.id === 'search-drawer') {
      reset();
    }
  });

  window.addEventListener('resize', () => {
    const nav = getNav();

    if (root.classList.contains(MODE_CLASS) && nav) {
      measure(nav);
    }
  });
})();
